import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Dustbin } from './components/Dustbin';
import update from 'immutability-helper';
import { Grid, Tooltip } from '@mui/material';
import { ItemTypes } from './types';
import { Autocomplete, Button, Card, Typography } from '@totalsoft/rocket-ui';
import WrappedLinearProgress from 'components/common/WrappedLinearProgress';

export const NewDocument = ({
  documents,
  saveDocument,
  onPreview,
  selectedDocument,
  pages,
  setPages,
  loading,
  selectedFiles,
  setSelectedPagesKeys
}) => {
  const { t } = useTranslation();
  const [attachment, setAttachment] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (selectedDocument) {
      setAttachment(selectedDocument.attachment);
      if (selectedDocument.pages[0].item.type === ItemTypes.FILE) {
        setPages(selectedDocument.pages);
      } else {
        setPages(
          update(selectedDocument.pages, {
            $push: [{ index: selectedDocument.pages.length, item: null }]
          })
        );
      }
    }
  }, [selectedDocument, setPages]);

  const accept = useCallback(() => {
    let items = pages.map(a => a.item).filter(x => x !== null);
    if (items.filter(a => a.type === ItemTypes.FILE).length > 0) return [ItemTypes.FILE];
    if (items.filter(a => a.type === ItemTypes.PAGE).length > 0) return [ItemTypes.PAGE];
    return [ItemTypes.PAGE, ItemTypes.FILE];
  }, [pages]);

  useEffect(() => {
    if (!scrollRef?.current) return;
    if (pages.length) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }
  }, [pages.length]);

  const handleFileReorder = useCallback(
    (dragIndex, hoverIndex) => {
      setPages(prevPages => {
        const updatedFiles = [...prevPages];
        const [draggedFile] = updatedFiles.splice(dragIndex, 1);
        updatedFiles.splice(hoverIndex, 0, draggedFile);
        return updatedFiles;
      });
    },
    [setPages]
  );

  const handleDrop = useCallback(
    (startIndex, item) => {
      let accepts = accept();

      if (startIndex < pages.length - 1 && selectedFiles.length > 0) {
        return;
      }
      if (pages[startIndex]?.item || item.type === ItemTypes.FILE) {
        setSelectedPagesKeys(prev => {
          if (pages.length > 0 && pages[0].item !== null) {
            const correlationIdToReplace = pages[0].item.correlationId;
            return prev.map(selectedPage => (selectedPage === correlationIdToReplace ? item.correlationId : selectedPage));
          } else {
            return [...prev, item.correlationId];
          }
        });
        setPages(
          update(pages, {
            [startIndex]: {
              item: {
                $set: item
              }
            }
          })
        );
      } else if (selectedFiles.length <= 1 && accepts.includes(item.type)) {
        setSelectedPagesKeys(prev => [...prev, item.storageKey]);
        setPages(
          update(pages, {
            [startIndex]: {
              item: {
                $set: { ...item, rotationAngle: 0 }
              }
            },
            $push: [{ index: pages.length, item: null }]
          })
        );
      } else {
        if (!accepts.includes(item.type)) return;
        setSelectedPagesKeys(prev => [...prev, ...new Set(selectedFiles.map(selectedFile => selectedFile.storageKey))]);
        setPages(prevPages => {
          let updatedPages = prevPages;
          selectedFiles.forEach((element, index) => {
            updatedPages = update(updatedPages, {
              [startIndex + index]: {
                item: {
                  $set: { ...element, rotationAngle: 0 }
                }
              },
              $push: [{ index: updatedPages.length, item: null }]
            });
          });
          return updatedPages;
        });
      }
    },
    [accept, setPages, selectedFiles, pages, setSelectedPagesKeys]
  );

  const handleSave = useCallback(() => {
    const documentPages = pages.filter(a => a.item != null);
    if (documentPages.length === 0) return;

    saveDocument({ attachment, pages: documentPages });
    setPages([...Array(1).keys()].map(a => ({ index: a, item: null })));
    setAttachment('');
  }, [pages, saveDocument, attachment, setPages]);

  const onRemove = useCallback(
    item => {
      const index = pages.findIndex(a => a.item === item);
      if (index >= 0) {
        if (item.type === ItemTypes.FILE) {
          setPages([{ index: 0, item: null }]);
        } else {
          setPages(update(pages, { $splice: [[index, 1]] }));
          setSelectedPagesKeys(prev => prev.filter(storageKey => storageKey !== item.storageKey));
        }
      }
    },
    [pages, setPages, setSelectedPagesKeys]
  );

  const handleChangeDocType = data => {
    setAttachment(data);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <Grid container alignItems='center' spacing={2}>
        <Grid item xs={4}>
          <Typography variant='subtitle1' color='textPrimary'>
            {t('BulkPdf.NewDocumentTitle')}
          </Typography>
          {loading && (
            <>
              <Tooltip title={t('BulkPdf.GenerateDocuments')} placement='bottom-start'></Tooltip>
              <WrappedLinearProgress />
            </>
          )}
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            fullWidth
            label={t('BulkPdf.NewDocumentType')}
            valueKey={'attachmentName'}
            labelKey={'attachmentName'}
            value={attachment}
            id='active'
            options={documents}
            onChange={handleChangeDocType}
          />
        </Grid>
        <Grid item xs={2} align='end'>
          <Button id={'save_document'} disabled={!attachment} onClick={handleSave}>
            {t('Save')}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <div style={{ overflowY: 'auto', height: 250, display: 'flex' }}>
            {pages.map(({ item }, index) => (
              <Dustbin
                key={'FilePage_' + index}
                accept={accept()}
                onRemove={onRemove}
                item={item}
                onDrop={item => handleDrop(index, item)}
                index={index}
                onPreview={onPreview}
                scrollRef={scrollRef}
                onFileReorder={handleFileReorder}
              />
            ))}
          </div>
        </Grid>
      </Grid>
    </Card>
  );
};

NewDocument.propTypes = {
  documents: PropTypes.array,
  saveDocument: PropTypes.func,
  onPreview: PropTypes.func.isRequired,
  selectedDocument: PropTypes.object,
  pages: PropTypes.array.isRequired,
  setPages: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  setSelectedPagesKeys: PropTypes.func.isRequired
};
